<template>
  <v-menu v-model="menu" :close-on-content-click="false" location="top">
    <template #activator="{ props }">
      <v-btn
        v-bind="props"
        icon
        variant="text"
        size="small"
        :color="updateAvailable ? 'warning' : 'grey'"
        :title="updateAvailable ? 'Update beschikbaar' : 'Versie informatie'"
      >
        <v-icon>
          {{ updateAvailable ? "mdi-update" : "mdi-information-outline" }}
        </v-icon>
        <v-badge v-if="updateAvailable" color="warning" dot floating></v-badge>
      </v-btn>
    </template>

    <v-card width="300">
      <v-card-title class="text-subtitle-1">
        <v-icon start>mdi-information</v-icon>
        Versie Informatie
      </v-card-title>

      <v-card-text>
        <v-row dense>
          <v-col cols="12">
            <div class="text-caption text-grey-darken-1">Versie</div>
            <div class="text-body-2 font-weight-medium">
              {{ version }}
              <v-chip
                v-if="!isProduction"
                size="x-small"
                color="orange"
                class="ml-2"
              >
                DEV
              </v-chip>
            </div>
          </v-col>

          <v-col cols="12">
            <div class="text-caption text-grey-darken-1">Gebouwd op</div>
            <div class="text-body-2">{{ formattedBuildDate }}</div>
          </v-col>

          <v-col cols="12">
            <div class="text-caption text-grey-darken-1">Build Hash</div>
            <div class="text-body-2 font-family-monospace">{{ buildHash }}</div>
          </v-col>

          <v-col cols="12">
            <div class="text-caption text-grey-darken-1">Modus</div>
            <div class="text-body-2">
              <v-chip
                :color="isProduction ? 'success' : 'warning'"
                size="small"
              >
                <v-icon start size="small">
                  {{ isProduction ? "mdi-check-circle" : "mdi-wrench" }}
                </v-icon>
                {{ isProduction ? "Productie" : "Ontwikkeling" }}
              </v-chip>
            </div>
          </v-col>

          <v-col v-if="lastUpdateCheck && isProduction" cols="12">
            <div class="text-caption text-grey-darken-1">Laatste controle</div>
            <div class="text-body-2">{{ formattedLastCheck }}</div>
          </v-col>
        </v-row>

        <v-alert
          v-if="!isProduction"
          type="info"
          density="compact"
          class="mt-3"
          border="start"
        >
          <div class="text-body-2">
            <v-icon start size="small">mdi-information</v-icon>
            Ontwikkelingsmodus - Update controle uitgeschakeld
          </div>
        </v-alert>

        <v-alert
          v-if="updateAvailable"
          type="warning"
          density="compact"
          class="mt-3"
          border="start"
        >
          <div class="text-body-2">
            <v-icon start size="small">mdi-download</v-icon>
            Nieuwe versie beschikbaar!
          </div>
          <div v-if="isProduction && timeDifference" class="text-caption mt-1">
            Versie is {{ timeDifference }} nieuwer
          </div>
          <div class="mt-2">
            <v-btn
              size="x-small"
              variant="text"
              class="text-caption"
              @click="resetUpdateState"
            >
              Verberg melding
            </v-btn>
          </div>
        </v-alert>

        <v-alert
          v-if="offlineMode"
          type="info"
          density="compact"
          class="mt-3"
          border="start"
        >
          <div class="text-body-2">
            <v-icon start size="small">mdi-wifi-off</v-icon>
            Offline modus actief
          </div>
        </v-alert>
      </v-card-text>

      <v-card-actions class="pa-3">
        <v-row dense>
          <v-col cols="12">
            <v-btn
              variant="text"
              size="small"
              :disabled="!isProduction"
              :loading="checking"
              block
              @click="checkForUpdates"
            >
              <v-icon start>mdi-refresh</v-icon>
              {{
                isProduction ? "Controleer Updates" : "Updates Uitgeschakeld"
              }}
            </v-btn>
          </v-col>

          <v-col v-if="updateAvailable && isProduction" cols="12">
            <v-btn
              color="warning"
              variant="elevated"
              size="small"
              block
              @click="refreshApp"
            >
              <v-icon start>mdi-refresh</v-icon>
              Vernieuwen
            </v-btn>
          </v-col>

          <v-col cols="12">
            <v-btn variant="text" size="small" block @click="menu = false">
              Sluiten
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import {
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  computed,
  getCurrentInstance,
} from "vue";
import moment from "moment";

export default defineComponent({
  name: "VersionInfo",
  setup() {
    // Reactive state
    const menu = ref(false);
    const updateAvailable = ref(false);
    const checking = ref(false);
    const lastUpdateCheck = ref(null);
    const offlineMode = ref(false);
    const currentVersion = ref(null);
    const latestVersion = ref(null);

    // Build info from global properties
    const buildInfo =
      getCurrentInstance()?.appContext.config.globalProperties.$buildInfo || {};
    const version = ref(buildInfo.version || "0.1.0");
    const buildTimestamp = ref(buildInfo.timestamp || new Date().toISOString());
    const buildHash = ref(buildInfo.hash || "dev");

    // Computed properties
    const isProduction = computed(() => import.meta.env.PROD);

    const formattedBuildDate = computed(() => {
      return moment(buildTimestamp.value).format("DD-MM-YYYY HH:mm");
    });

    const formattedLastCheck = computed(() => {
      return lastUpdateCheck.value
        ? moment(lastUpdateCheck.value).fromNow()
        : "Nooit";
    });

    const timeDifference = computed(() => {
      if (!latestVersion.value || !buildTimestamp.value) return null;

      const currentTime = new Date(buildTimestamp.value).getTime();
      const latestTime = new Date(latestVersion.value).getTime();
      const diffMinutes = Math.round((latestTime - currentTime) / (1000 * 60));

      if (diffMinutes < 1) return "Minder dan 1 minuut";
      if (diffMinutes === 1) return "1 minuut";
      if (diffMinutes < 60) return `${diffMinutes} minuten`;

      const diffHours = Math.round(diffMinutes / 60);
      if (diffHours === 1) return "1 uur";
      if (diffHours < 24) return `${diffHours} uur`;

      const diffDays = Math.round(diffHours / 24);
      return diffDays === 1 ? "1 dag" : `${diffDays} dagen`;
    });

    // Service Worker related functions
    let serviceWorkerRegistration = null;

    const checkForUpdates = async () => {
      // Skip updates in development mode
      if (!isProduction.value) {
        console.log("VersionInfo: Skipping update check in development mode");
        return;
      }

      checking.value = true;
      lastUpdateCheck.value = new Date();

      try {
        // Reset update available state before checking
        updateAvailable.value = false;

        // Check if service worker is available
        if ("serviceWorker" in navigator) {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            serviceWorkerRegistration = registration;

            // Check for updates
            await registration.update();

            // Get current version from service worker
            const channel = new MessageChannel();
            registration.active?.postMessage({ type: "GET_VERSION" }, [
              channel.port2,
            ]);

            channel.port1.onmessage = (event) => {
              if (event.data.type === "VERSION_INFO") {
                currentVersion.value = event.data.version;
                console.log("Current SW version:", currentVersion.value);
              }
            };
          }
        }

        // Check via version.json endpoint with cache-busting
        try {
          const response = await fetch("/version.json?t=" + Date.now(), {
            cache: "no-cache",
            headers: {
              "Cache-Control": "no-cache, no-store, must-revalidate",
              Pragma: "no-cache",
            },
          });

          if (response.ok) {
            const versionData = await response.json();
            latestVersion.value = versionData.buildTimestamp;

            console.log("Version comparison:", {
              current: buildTimestamp.value,
              latest: latestVersion.value,
              currentType: typeof buildTimestamp.value,
              latestType: typeof latestVersion.value,
            });

            // Compare versions - only show update if server has a newer timestamp
            if (latestVersion.value && buildTimestamp.value) {
              const currentTime = new Date(buildTimestamp.value).getTime();
              const latestTime = new Date(latestVersion.value).getTime();
              const timeDiffMinutes = (latestTime - currentTime) / (1000 * 60);

              console.log("Version comparison details:", {
                current: buildTimestamp.value,
                latest: latestVersion.value,
                currentTime: currentTime,
                latestTime: latestTime,
                difference: latestTime - currentTime,
                differenceMinutes: timeDiffMinutes,
              });

              // Only consider it an update if the server version is significantly newer
              // This accounts for CloudFront/CDN caching scenarios where rapid deployments
              // might cause version.json to be newer than cached static assets
              const MIN_UPDATE_THRESHOLD_MINUTES = 2; // Minimum 2 minutes difference

              if (timeDiffMinutes > MIN_UPDATE_THRESHOLD_MINUTES) {
                updateAvailable.value = true;
                console.log(
                  "Update available - server version is significantly newer:",
                  {
                    timeDifferenceMinutes: timeDiffMinutes,
                    threshold: MIN_UPDATE_THRESHOLD_MINUTES,
                  },
                );
              } else if (timeDiffMinutes > 0) {
                console.log(
                  "Server version is newer but within CDN cache threshold, ignoring:",
                  {
                    timeDifferenceMinutes: timeDiffMinutes,
                    threshold: MIN_UPDATE_THRESHOLD_MINUTES,
                    reason: "Likely CloudFront/CDN caching scenario",
                  },
                );
                updateAvailable.value = false;
              } else {
                console.log(
                  "No update needed - current version is same or newer",
                );
                updateAvailable.value = false;
              }
            }
          } else {
            console.log(
              "Could not fetch version.json, status:",
              response.status,
            );
          }
        } catch (error) {
          console.log("Could not check version.json:", error);
          offlineMode.value = true;
        }
      } catch (error) {
        console.error("Error checking for updates:", error);
        updateAvailable.value = false;
      } finally {
        checking.value = false;
      }
    };

    const refreshApp = () => {
      // Clear all caches and refresh
      if ("caches" in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }

      // Send skip waiting to service worker
      if (serviceWorkerRegistration?.waiting) {
        serviceWorkerRegistration.waiting.postMessage({ type: "SKIP_WAITING" });
      }

      // Force reload
      window.location.reload();
    };

    const resetUpdateState = () => {
      updateAvailable.value = false;
      console.log("Update state manually reset");
    };

    // Listen for service worker updates
    const handleSWMessage = (event) => {
      if (event.data.type === "SW_UPDATED") {
        console.log("Service Worker updated message received:", event.data);
        // Only set update available if we get a version that's actually different
        if (event.data.version && event.data.version !== buildTimestamp.value) {
          updateAvailable.value = true;
          console.log("Service Worker version change confirmed:", {
            current: buildTimestamp.value,
            new: event.data.version,
          });
        } else {
          console.log("Service Worker updated but version is same, ignoring");
        }
      }
    };

    // Listen for online/offline events
    const handleOnline = () => {
      offlineMode.value = false;
      checkForUpdates();
    };

    const handleOffline = () => {
      offlineMode.value = true;
    };

    onMounted(() => {
      let updateInterval = null;

      // Only do update checks in production mode
      if (import.meta.env.PROD) {
        // Delay initial update check to avoid false positives on first load
        setTimeout(checkForUpdates, 5000); // Wait 5 seconds instead of 1

        // Set up periodic checks (every 5 minutes)
        updateInterval = setInterval(checkForUpdates, 5 * 60 * 1000);

        // Listen for service worker messages
        navigator.serviceWorker?.addEventListener("message", handleSWMessage);
      } else {
        console.log("VersionInfo: Development mode - skipping update checks");
      }

      // Always listen for online/offline events
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);

      // Set initial offline state
      offlineMode.value = !navigator.onLine;

      // Single cleanup function for all listeners
      onUnmounted(() => {
        if (updateInterval) {
          clearInterval(updateInterval);
        }
        navigator.serviceWorker?.removeEventListener(
          "message",
          handleSWMessage,
        );
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      });
    });

    return {
      menu,
      updateAvailable,
      checking,
      lastUpdateCheck,
      offlineMode,
      version,
      formattedBuildDate,
      formattedLastCheck,
      buildHash,
      buildTimestamp,
      latestVersion,
      checkForUpdates,
      refreshApp,
      resetUpdateState,
      isProduction,
      timeDifference,
    };
  },
});
</script>
